import { VideoCameraOutlined } from '@ant-design/icons'
import { useToggle } from 'ahooks'
import { Button, Tag, Tooltip, Typography } from 'antd'
import Avatar from 'antd/lib/avatar/avatar'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Timestamp } from '@/components'
import {
  AnnouncementReplyType,
  RequestedPostFragment,
  useAnnouncementReplyCreateMutation,
  usePostUpdatedSubscription,
  useViewerAnnouncementUpdateMutation,
} from '@/generated/graphql'
import { useReadPost } from '@/modules/post/post-card/post-card.hooks'

import { AnnouncementVideoModal } from './announcement-video-modal.component'
import s from './announcement.module.less'

enum AnnouncementTypeColor {
  Announcement = '#B14FB2',
  Feature = '#F05656',
  Fix = '#FFC857',
  Improvement = '#5EA958',
}

export const AnnouncementCard: React.FC<{ post: RequestedPostFragment }> = ({ post }) => {
  const { t } = useTranslation()
  const [visible, { toggle }] = useToggle()

  const [createAnnouncementReply] = useAnnouncementReplyCreateMutation()
  const [updateAnnouncement] = useViewerAnnouncementUpdateMutation()

  const ref = useReadPost(post)
  const announcement = post.announcement

  usePostUpdatedSubscription({ variables: { postId: post.postId } })

  if (!announcement) {
    return null
  }

  const onClick = (content: string, replyType: AnnouncementReplyType) => {
    createAnnouncementReply({
      variables: {
        input: {
          announcementId: announcement.announcementId,
          content,
          replyType,
        },
      },
    })
  }

  const header = (
    <div className={s.cardHeader}>
      <Tag className={s.tag} color={AnnouncementTypeColor[announcement.type]}>
        {t(`post.announcement.type.${announcement.type}`)}
      </Tag>
      <span className={s.time}>
        <Timestamp time={post.updatedAt} />
      </span>
    </div>
  )

  const content = (
    <div className={s.cardContent}>
      <Typography className={s.title}>{announcement.title}</Typography>
      <div className={s.content}>{announcement.content}</div>
    </div>
  )

  const actions = (
    <div className={s.cardActions}>
      {post.files.length > 0 && (
        <div className={s.buttons}>
          <Button type='primary' shape='round' icon={<VideoCameraOutlined />} onClick={() => toggle()}>
            {t('post.announcement.button.wantMore')}
          </Button>
          <Button shape='round' onClick={() => updateAnnouncement({ variables: { shouldBeAnnounced: false } })}>
            {t('post.announcement.button.gotIt')}
          </Button>
        </div>
      )}
      {announcement.repliedType ? (
        <div className={s.message}>{t('post.announcement.feedback.message')}</div>
      ) : (
        <div className={s.actions}>
          <Tooltip key={AnnouncementReplyType.Negative} title={t('post.announcement.action.negative')}>
            <Avatar
              onClick={() => onClick(t('post.announcement.action.negative'), AnnouncementReplyType.Negative)}
              size={40}
              shape='square'
              src='https://agora-web-cdn.oss-cn-beijing.aliyuncs.com/tale/backgrounds/announcement-negative.svg'
            />
          </Tooltip>
          <Tooltip key={AnnouncementReplyType.Neutrality} title={t('post.announcement.action.neutrality')}>
            <Avatar
              onClick={() => onClick(t('post.announcement.action.neutrality'), AnnouncementReplyType.Neutrality)}
              size={40}
              shape='square'
              src='https://agora-web-cdn.oss-cn-beijing.aliyuncs.com/tale/backgrounds/announcement-neutrality.svg'
            />
          </Tooltip>
          <Tooltip key={AnnouncementReplyType.Positive} title={t('post.announcement.action.positive')}>
            <Avatar
              onClick={() => onClick(t('post.announcement.action.positive'), AnnouncementReplyType.Positive)}
              size={40}
              shape='square'
              src='https://agora-web-cdn.oss-cn-beijing.aliyuncs.com/tale/backgrounds/announcement-positive.svg'
            />
          </Tooltip>
        </div>
      )}
    </div>
  )

  return (
    <div ref={ref} className={s.card}>
      {header}
      {content}
      {actions}
      <AnnouncementVideoModal files={post.files} visible={visible} toggle={toggle} />
    </div>
  )
}
